import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { SiteContactLinkPublic, SiteSocialLinkPublic, FooterSettings } from "@tih/shared";
import { FOOTER_SETTINGS_DEFAULT } from "@tih/shared";
import { useEventContent } from "../context/EventContentContext";
import { formatShortOvernightFooter } from "../lib/eventDisplay";
import { fetchSiteContacts, fetchSiteSocialLinks, fetchFooterSettings } from "../api/client";
import { FooterClouds } from "./FooterClouds";

function isExternalHref(href: string): boolean {
  return /^https?:\/\//i.test(href);
}

export function Footer() {
  const content = useEventContent();
  const eventLinkLabel = formatShortOvernightFooter(content.date);
  const [contacts, setContacts] = useState<SiteContactLinkPublic[]>([]);
  const [socialLinks, setSocialLinks] = useState<SiteSocialLinkPublic[]>([]);
  const [footerSettings, setFooterSettings] = useState<FooterSettings>(FOOTER_SETTINGS_DEFAULT);

  useEffect(() => {
    fetchSiteContacts()
      .then(setContacts)
      .catch(() => {
        /* keep footer usable without contact links */
      });
    fetchSiteSocialLinks()
      .then(setSocialLinks)
      .catch(() => {
        /* keep footer usable without social links */
      });
    fetchFooterSettings()
      .then(setFooterSettings)
      .catch(() => {
        /* keep footer usable with defaults */
      });
  }, []);

  return (
    <footer className="footer">
      <FooterClouds />
      <div className="foot-inner">
        <div className="foot-brand">
          <Link to="/" aria-label="Home">
            <img src="/assets/logo.png" alt="Together In Heaven" className="foot-logo" />
          </Link>
        </div>
        <div
          className={`foot-cols${socialLinks.length > 0 ? "" : " foot-cols--no-social"}`}
        >
          <div>
            <h5>Event</h5>
            <Link to="/event">{eventLinkLabel}</Link>
            <Link to="/event">Program</Link>
            <Link to="/event">Venue</Link>
            <Link to="/live">Livestream</Link>
          </div>
          <div>
            <h5>Ministry</h5>
            <Link to="/dorothy">About us</Link>
            <Link to="/dorothy">Dorothy Ashaba</Link>
            <Link to="/participate">Participate</Link>
            <Link to="/testimonials">Testimonies</Link>
            <Link to="/partner">Partner</Link>
            <Link to="/give">Give</Link>
          </div>
          <div>
            <h5>Explore</h5>
            <Link to="/blog">Blog</Link>
            <Link to="/gallery">Gallery</Link>
            <Link to="/videos">Videos</Link>
            <Link to="/contact">Contact</Link>
          </div>
          <div>
            <h5>Contact</h5>
            {contacts.map((link) => (
              <a
                key={link.id}
                href={link.href}
                {...(isExternalHref(link.href)
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : {})}
              >
                {link.label}
              </a>
            ))}
            {contacts.length === 0 && (
              <Link to="/contact">Get in touch</Link>
            )}
          </div>
          {socialLinks.length > 0 && (
            <div>
              <h5>Social</h5>
              {socialLinks.map((link) => (
                <a
                  key={link.id}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link.label}
                </a>
              ))}
            </div>
          )}
        </div>
        <div className="foot-bottom">
          <span>{footerSettings.copyright}</span>
          <span className="foot-verse">{footerSettings.verse}</span>
        </div>
      </div>
    </footer>
  );
}
